import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../../../context/app';
import ButtonStyled from '../button/Button';
import Icon from '../icon/Icon';
import { IconType } from '../icon/types';
import { IconWithCoordinates } from '../youAreHerePin/IconWithCoordinates';
import styles from './LegTwoVisualStep.module.scss';

type LegTwoVisualStepsProps = {
  map: string | undefined;
  onMapLoad: (event: any) => void;
  showYouAreHere: boolean;
  startingPoint: { x: number; y: number };
  endingPoint: { x: number; y: number };
  isLastStep: boolean | undefined;
  svgPath: string;
  startArrowPath: string;
  endArrowPath: string;
  index: number;
  formattedInstruction: string;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  imgHeight: number;
  imgWidth: number;
};

export const LegTwoVisualStepMobile = React.forwardRef<
  HTMLImageElement,
  LegTwoVisualStepsProps
>(
  (
    {
      map,
      onMapLoad,
      showYouAreHere,
      startingPoint,
      endingPoint,
      isLastStep,
      svgPath,
      startArrowPath,
      endArrowPath,
      index,
      formattedInstruction,
      goToPreviousStep,
      goToNextStep,
      imgHeight,
      imgWidth,
    },
    ref,
  ) => {
    const { directions, showPrintDirectionView } = useContext(AppContext);
    const { t } = useTranslation('common');

    return (
      <div className={styles.legTwoMobileMapAndDirections}>
        <div className={styles.legTwoMobileMapContainer}>
          <img
            className={` ${styles.mapMobileImg} `}
            src={map}
            ref={ref}
            onLoad={onMapLoad}
            alt={'stepDirections'}
          />
          {showYouAreHere && (
            <IconWithCoordinates
              coordinates={startingPoint}
              iconType={IconType.YOU_ARE_HERE}
              size={125}
            />
          )}
          {isLastStep && (
            <IconWithCoordinates
              coordinates={endingPoint}
              iconType={IconType.PIN_ACTIVE}
              size={50}
            />
          )}
          <svg className={styles.overlay} height={imgHeight} width={imgWidth}>
            {!showYouAreHere && (
              <circle
                cx={startingPoint.x}
                cy={startingPoint.y}
                r="10"
                fill="white"
                stroke="black"
                strokeWidth="4"
              />
            )}
            {!isLastStep && (
              <circle
                cx={endingPoint.x}
                cy={endingPoint.y}
                r="10"
                fill="#0D6AFF"
                stroke="#0D6AFF"
                strokeWidth="4"
              />
            )}
            {showPrintDirectionView ? (
              <>
                <defs>
                  <marker
                    id="arrow"
                    viewBox="0 0 10 10"
                    refY="2"
                    markerWidth="10"
                    markerHeight="10"
                    orient="auto"
                  >
                    <path d="M0,0 L4,2 0,4" fill="#0D6AFF" />
                  </marker>
                </defs>
                <path
                  id="any"
                  d={svgPath}
                  stroke="#0D6AFF"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="8 8"
                  strokeMiterlimit="6.67"
                  fill="none"
                />
                <path
                  id="start-arrow"
                  d={startArrowPath}
                  fill="none"
                  stroke="#0D6AFF"
                  strokeWidth="4"
                  markerEnd="url(#arrow)"
                />
                <path
                  id="end-arrow"
                  d={endArrowPath}
                  fill="none"
                  stroke="#0D6AFF"
                  strokeWidth="4"
                  markerStart="url(#arrow)"
                />
              </>
            ) : (
              <path
                id="any"
                d={svgPath}
                stroke="#0D6AFF"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="12 12"
                strokeMiterlimit="10"
                fill="none"
                strokeDashoffset="1"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  values="100;0"
                  dur="3s"
                  calcMode="linear"
                  repeatCount="indefinite"
                />
              </path>
            )}
          </svg>
        </div>
        <div className={styles.legTwoMobileDirections}>
          <div className={styles.mobileLegTwoDirectionRow}>
            <span className={styles.newMobileInstruction}>
              {index + 1}. {formattedInstruction}
            </span>
          </div>
          <div className={styles.mobileLegTwoDirectionButtons}>
            <ButtonStyled
              onClick={goToPreviousStep}
              disabled={index - 1 <= -1 && !directions}
              flexWide
              uppercase
            >
              <Icon icon={IconType.LEFT} size={32} />
              {t('campusNavigation.directions.previous')}
            </ButtonStyled>
            <ButtonStyled onClick={goToNextStep} flexWide uppercase>
              {isLastStep
                ? t('campusNavigation.directions.exit')
                : t('campusNavigation.directions.continue')}
              {!isLastStep && <Icon icon={IconType.RIGHT} size={32} />}
            </ButtonStyled>
          </div>
        </div>
      </div>
    );
  },
);
